import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Admin" path="/admin/login" />
      <div className="min-h-screen bg-ptf-primary text-ptf-bg grid place-items-center px-5 grain">
        <div className="relative z-10 w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <span className="grid place-items-center h-10 w-10 rounded-full border border-ptf-bg/40">
              <span className="h-3 w-3 rounded-full bg-ptf-accent" />
            </span>
            <span className="leading-none">
              <span className="block font-display font-extrabold text-xl tracking-tight uppercase">Planet Trotter</span>
              <span className="block text-[0.6rem] tracking-[0.34em] uppercase text-ptf-bg/60 mt-0.5">Foundation · Admin</span>
            </span>
          </div>

          <h1 className="font-display uppercase text-4xl tracking-tight mb-8">Sign in</h1>

          <form onSubmit={submit} data-testid="admin-login-form" className="space-y-6">
            <div>
              <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-bg/60">Email</label>
              <input required type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent border-b border-ptf-bg/30 py-3 outline-none focus:border-ptf-accent transition-colors" />
            </div>
            <div>
              <label className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-bg/60">Password</label>
              <input required type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-transparent border-b border-ptf-bg/30 py-3 outline-none focus:border-ptf-accent transition-colors" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-300">{error}</p>}
            <button type="submit" data-testid="login-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-ptf-accent text-ptf-primary py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:brightness-95 transition disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Signing in" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
